import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Send, Loader2, Mail, Users, AlertTriangle, Sparkles, Cake, Filter, Gift, Phone } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * CampaignDialog — envía una campaña de email a un segmento de clientes.
 *
 * Flujo:
 * 1. Usuario selecciona filtros (segmento) + plantilla predefinida.
 * 2. Pulsa "Previsualizar" → POST /loyalty/campaign/preview → muestra nº destinatarios + sample.
 * 3. Edita subject/body (con variables {name}, {points}, {restaurant}, {bonus_pts}).
 * 4. Pulsa "Enviar ahora" → POST /loyalty/campaign/send con confirmación.
 *
 * Props: open, onClose, restaurantId, levels[], onSent()
 */

const TEMPLATES = {
  winback: {
    label: "Recuperar inactivos",
    subject: "Te echamos de menos en {restaurant}",
    body:
      "Hola {name},\n\n" +
      "Hace tiempo que no te vemos. Queremos agradecerte tu confianza con {bonus_pts} puntos extra " +
      "que ya están disponibles en tu tarjeta para usar cuando quieras.\n\n" +
      "¡Te esperamos pronto!",
  },
  birthday: {
    label: "Cumpleaños",
    subject: "¡Felicidades {name}! Regalo de cumpleaños en {restaurant}",
    body:
      "Hola {name},\n\n" +
      "Es tu cumpleaños y queremos celebrarlo contigo. Te regalamos {bonus_pts} puntos extra " +
      "y una sorpresa al presentar esta campaña en el restaurante.\n\n" +
      "¡Felicidades!",
  },
  new_reward: {
    label: "Nueva recompensa",
    subject: "Nueva recompensa disponible en {restaurant}",
    body:
      "Hola {name},\n\n" +
      "Hemos añadido una recompensa nueva al programa de fidelización que " +
      "creemos que te va a encantar. Échale un vistazo en tu tarjeta digital.\n\n" +
      "Tu saldo actual: {points} pts.",
  },
  custom: {
    label: "Personalizada",
    subject: "{restaurant}",
    body: "Hola {name},\n\n...\n\nTu saldo: {points} pts.",
  },
};

const FILTER_CHIPS = [
  { k: "has_phone",           label: "Con teléfono",            Icon: Phone },
  { k: "birthday_this_month", label: "Cumple este mes",         Icon: Cake },
  { k: "inactive_30d",        label: "Inactivos >30 días",      Icon: Filter },
  { k: "has_points",          label: "Con puntos acumulados",   Icon: Gift },
];

export default function CampaignDialog({ open, onClose, restaurantId, levels = [], onSent }) {
  const [template, setTemplate] = useState("winback");
  const [subject, setSubject] = useState(TEMPLATES.winback.subject);
  const [body, setBody] = useState(TEMPLATES.winback.body);
  const [bonusPoints, setBonusPoints] = useState(0);
  const [filters, setFilters] = useState({
    has_email: true,
    has_phone: false,
    birthday_this_month: false,
    inactive_30d: false,
    has_points: false,
    level_name: "",
  });
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const reset = useCallback(() => {
    setTemplate("winback");
    setSubject(TEMPLATES.winback.subject);
    setBody(TEMPLATES.winback.body);
    setBonusPoints(0);
    setFilters({
      has_email: true, has_phone: false, birthday_this_month: false,
      inactive_30d: false, has_points: false, level_name: "",
    });
    setPreview(null);
    setConfirmOpen(false);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleTemplateChange = (v) => {
    setTemplate(v);
    setSubject(TEMPLATES[v].subject);
    setBody(TEMPLATES[v].body);
  };

  const toggleChip = (k) => {
    setFilters((f) => ({ ...f, [k]: !f[k] }));
    setPreview(null);
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const payload = { ...filters };
      if (!payload.level_name) delete payload.level_name;
      const r = await api.previewLoyaltyCampaign(restaurantId, payload);
      setPreview(r);
    } catch (err) {
      toast.error(err.message || "No se pudo calcular el segmento");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Asunto y cuerpo son obligatorios");
    if (!preview || preview.count === 0) return toast.error("No hay destinatarios en el segmento");
    setSending(true);
    try {
      const payload = { ...filters };
      if (!payload.level_name) delete payload.level_name;
      const r = await api.sendLoyaltyCampaign(restaurantId, {
        filters: payload,
        template,
        subject: subject.trim(),
        body: body.trim(),
        bonus_points: Number(bonusPoints) || 0,
      });
      if (!r.resend_configured) {
        toast.warning(`Resend no configurado. Se registró la campaña (${r.recipients} contactos) pero no se envió ningún email.`);
      } else if (r.errors > 0) {
        toast.warning(`Enviados ${r.sent} · ${r.errors} errores de ${r.recipients} destinatarios`);
      } else {
        toast.success(`Campaña enviada a ${r.sent} clientes${r.bonus_points_applied > 0 ? ` · ${r.bonus_points_applied} pts aplicados` : ""}`);
      }
      onSent?.(r);
      setConfirmOpen(false);
      onClose();
    } catch (err) {
      toast.error(err.message || "Error al enviar la campaña");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" /> Enviar campaña de email
            </DialogTitle>
            <DialogDescription>
              Envía un email masivo al segmento elegido. Se usarán las variables{" "}
              <code className="text-xs bg-muted px-1 rounded">{"{name}"}</code>,{" "}
              <code className="text-xs bg-muted px-1 rounded">{"{points}"}</code>,{" "}
              <code className="text-xs bg-muted px-1 rounded">{"{restaurant}"}</code>,{" "}
              <code className="text-xs bg-muted px-1 rounded">{"{bonus_pts}"}</code>.
            </DialogDescription>
          </DialogHeader>

          {/* Segmento */}
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Segmento
            </Label>
            <div className="flex flex-wrap gap-1.5" data-testid="campaign-filters">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground opacity-70">
                <Mail className="w-3 h-3" /> Con email (obligatorio)
              </div>
              {FILTER_CHIPS.map(({ k, label, Icon }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleChip(k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition ${
                    filters[k]
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/70"
                  }`}
                  data-testid={`campaign-chip-${k}`}
                >
                  <Icon className="w-3 h-3" /> {label}
                </button>
              ))}
            </div>
            {levels?.length > 0 && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground shrink-0">Nivel:</Label>
                <Select
                  value={filters.level_name || "__all__"}
                  onValueChange={(v) => { setFilters((f) => ({ ...f, level_name: v === "__all__" ? "" : v })); setPreview(null); }}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="campaign-level-select"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos los niveles</SelectItem>
                    {levels.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={loadPreview}
              disabled={loadingPreview}
              data-testid="campaign-preview-btn"
            >
              {loadingPreview ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Users className="w-3.5 h-3.5 mr-1.5" />}
              Calcular destinatarios
            </Button>

            {preview && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="campaign-preview">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-primary">{preview.count}</span>
                  <span className="text-xs text-muted-foreground">destinatario{preview.count === 1 ? "" : "s"} estimado{preview.count === 1 ? "" : "s"}</span>
                </div>
                {preview.sample?.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    Ejemplo: {preview.sample.map((s) => s.name).join(", ")}{preview.count > preview.sample.length ? "…" : ""}
                  </div>
                )}
                {preview.count === 0 && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Ningún cliente cumple los filtros seleccionados.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Plantilla */}
          <div className="space-y-3 pt-2 border-t">
            <div className="space-y-2">
              <Label>Plantilla</Label>
              <Select value={template} onValueChange={handleTemplateChange}>
                <SelectTrigger data-testid="campaign-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TEMPLATES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Asunto</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Asunto del email…"
                data-testid="campaign-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Cuerpo del email</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                placeholder="Hola {name}…"
                className="font-mono text-xs"
                data-testid="campaign-body-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                Bonus de puntos (opcional)
              </Label>
              <Input
                type="number"
                min={0}
                max={10000}
                value={bonusPoints}
                onChange={(e) => setBonusPoints(e.target.value)}
                placeholder="0"
                data-testid="campaign-bonus-input"
              />
              <p className="text-xs text-muted-foreground">
                Si es &gt; 0, se sumará a cada cliente del segmento y se registrará en su historial.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="campaign-cancel-btn">Cancelar</Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={sending || !preview || preview.count === 0 || !subject.trim() || !body.trim()}
              data-testid="campaign-send-btn"
            >
              <Send className="w-4 h-4 mr-1.5" />
              Enviar {preview ? `a ${preview.count}` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="campaign-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Confirmar envío
            </DialogTitle>
            <DialogDescription>
              Vas a enviar esta campaña a <b>{preview?.count ?? 0}</b> cliente{preview?.count === 1 ? "" : "s"}.
              {Number(bonusPoints) > 0 && ` Además, se sumarán ${bonusPoints} puntos a cada uno.`}
              <br />Esta acción no puede deshacerse.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>Cancelar</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="campaign-confirm-send-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Enviar ahora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
